import React, { useEffect } from "react";
import moment from "moment";
import { injectIntl } from "react-intl";
import DataGrid, { DataGridItem } from "components/data-grid";
import FormDatePicker from "components/form/form-datepicker";
import PropTypes from "prop-types";
import DurationHumanizer from "components/duration-humanizer";
import { RestrictedToAdmin } from "components/auth";
import FormAdminFields from "../../common-forms/restricted-admin-form";
import { eventStatusTypes } from "pages/event/utils";
import { PageSection } from "components/page";
import { SportForm } from "../../common-forms";
import FormCheckbox from "components/form/form-checkbox";
import { LiveStreamsData } from "../../../details-page";
import PageHeaderBar from "../../../../../components/page/components/page-header-bar/page-header-bar";

const EditEventStartedForm = ({
  eventToEdit,
  hasAdminPrivileges,
  isAuthorizedRecording,
  intl,
  values,
  handleChange,
  eventCustomerId,
  customer,
  ...props
}) => {
  const canEditEnd = moment(values.endDate).isAfter(moment()) && values.status !== eventStatusTypes.FINISHED;
  const isSportEvent = values && values.type && values.type.isSportEvent;

  const handleChangeCheckboxTestEvent = valueTest => {
    handleChange("visible")(!valueTest);
    handleChange("billable")(!valueTest);
  };

  useEffect(
    () => {
      if (!!values.isInternalTest) {
        handleChangeCheckboxTestEvent(values.isInternalTest);
      }
    },
    [values.isInternalTest],
  );

  return (
    <>
      {(hasAdminPrivileges || canEditEnd) && (
        <PageSection>
          <DataGrid>
            <DataGridItem>
              <FormDatePicker
                value={values.endDate}
                label={intl.formatMessage({ id: "common.until" })}
                id="endDate"
                name="endDate"
                onChange={handleChange}
              />
            </DataGridItem>
            <DataGridItem big>
              <DurationHumanizer start={values.startDate} end={values.endDate} />
            </DataGridItem>
          </DataGrid>
        </PageSection>
      )}
      <PageSection>
        <DataGrid>
          <RestrictedToAdmin>
            <DataGridItem>
              <FormCheckbox
                id="isInternalTest"
                name="isInternalTest"
                value="isInternalTest"
                checked={values.isInternalTest}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.isInternalTest" })}
              />
            </DataGridItem>
            <DataGridItem>
              <FormCheckbox
                id="visible"
                name="visible"
                value="visible"
                checked={values.visible}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.visible" })}
              />
            </DataGridItem>
            <DataGridItem>
              <FormCheckbox
                id="billable"
                name="billable"
                value="billable"
                checked={values.billable}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.billable" })}
              />
            </DataGridItem>
          </RestrictedToAdmin>
          {!!props.initialValues?.localRecInfo && isAuthorizedRecording && (
            <DataGridItem>
              <FormCheckbox
                id="authorizedRecording"
                name="authorizedRecording"
                value="authorizedRecording"
                checked={values.authorizedRecording}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.authorizedRecording" })}
              />
            </DataGridItem>
          )}
          {customer?.contract?.localRecAvailable && (
            <DataGridItem>
              <FormCheckbox
                id="localRec"
                name="localRec"
                value="localRec"
                checked={values.localRec}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.local.rec" })}
                disabled
              />
            </DataGridItem>
          )}
          {customer?.contract?.allowQod && (
            <DataGridItem>
              <FormCheckbox
                id="activeQod"
                name="activeQod"
                value="activeQod"
                checked={values.activeQod}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.activeQod" })}
                disabled
              />
            </DataGridItem>
          )}
        </DataGrid>
      </PageSection>
      {!props.initialValues?.localRecInfo && eventToEdit.liveStreams && (
        <>
          <PageHeaderBar subtitle title="app.page.event.form.livestreams" />
          <PageSection>
            <LiveStreamsData event={eventToEdit} />
          </PageSection>
        </>
      )}
      {!props.initialValues?.localRecInfo && isSportEvent && (
        <SportForm
          eventToEdit={eventToEdit}
          eventCustomerId={eventCustomerId}
          handleChange={handleChange}
          values={values}
        />
      )}
      <FormAdminFields values={values} handleChange={handleChange} />
    </>
  );
};

EditEventStartedForm.propTypes = {
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Otros datos del evento a editar que no se envían en el form
   */
  eventToEdit: PropTypes.object,
  /**
   * Indica si el usuario tiene privilegios de administrador
   */
  hasAdminPrivileges: PropTypes.bool,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
  /**
   * Customer id del contrato del evento
   */
  eventCustomerId: PropTypes.string,
};

export default injectIntl(EditEventStartedForm);
